import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import constants from '../../../lib/constants'
import { BeatLoader } from 'react-spinners'
import { getAverageWBC, countOutOfRange, areAllInitialJobsComplete, countTotalOutOfRange } from 'store/qc'
import { Link } from 'react-router'

class ResultsView extends React.Component {
  static propTypes = {
    qcResults: PropTypes.shape({
      low_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      })),
      normal_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      })),
      high_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      }))
    }),
    level: PropTypes.oneOf(['low', 'normal', 'high']),
    controlMaterial: PropTypes.shape({
      low: PropTypes.shape({
        wbc_min: PropTypes.number,
        wbc_max: PropTypes.number,
        neutrophils_min: PropTypes.number,
        neutrophils_max: PropTypes.number
      }),
      normal: PropTypes.shape({
        wbc_min: PropTypes.number,
        wbc_max: PropTypes.number,
        neutrophils_min: PropTypes.number,
        neutrophils_max: PropTypes.number
      }),
      high: PropTypes.shape({
        wbc_min: PropTypes.number,
        wbc_max: PropTypes.number,
        neutrophils_min: PropTypes.number,
        neutrophils_max: PropTypes.number
      }),
      flowTimes: PropTypes.shape({})
    }),
    flowTimes: PropTypes.shape({}),
    runIndividualQc: PropTypes.func,
    deleteQcJob: PropTypes.func,
    cancelQcJob: PropTypes.func,
    setFlowTime: PropTypes.func,
    allowRunAgain: PropTypes.bool,
    markRunAgain: PropTypes.func,
    onRetestLevel: PropTypes.func
  }

  static defaultProps = {
    qcResults: {
      low_jobs: [],
      normal_jobs: [],
      high_jobs: [],
      flow_times: {}
    },
    level: 'low',
    controlMaterial: {
      low: {
        wbc_min: 2,
        wbc_max: 4,
        neutrophils_min: 20,
        neutrophils_max: 80
      },
      normal: {
        wbc_min: 2,
        wbc_max: 4,
        neutrophils_min: 20,
        neutrophils_max: 80
      },
      high: {
        wbc_min: 2,
        wbc_max: 4,
        neutrophils_min: 20,
        neutrophils_max: 80
      }
    },
    flowTimes: {},
    runIndividualQc: _.noop,
    deleteQcJob: _.noop,
    cancelQcJob: _.noop,
    setFlowTime: _.noop,
    markRunAgain: _.noop,
    allowRunAgain: true,
    onRetestLevel: _.noop
  }

  constructor (ownProps) {
    super(ownProps)
    this.state = {}
  }



  rerunQc = (qcId, level, job) => {
    const { runIndividualQc, deleteQcJob, markRunAgain } = this.props
    markRunAgain()
    let deletePromise = null
    if (job && job.job_id) {
      deletePromise = deleteQcJob(qcId, job)
    } else {
      deletePromise = Promise.resolve()
    }
    deletePromise
    .then(() => {
      runIndividualQc(qcId, level)
    })
  }

  runQc = (qcId, level) => {
    const { runIndividualQc } = this.props
    runIndividualQc(qcId, level)
  }

  getMaxBiasJob = () => {
    const { qcResults, level } = this.props
    const mean = getAverageWBC(qcResults[`${level}_jobs`])
    let maxBias = 0
    let maxBiasJob = null
    _.forEach(qcResults[`${level}_jobs`], (job) => {
      if (job && job.wbc_count) {
        const bias = Math.abs(mean - job.wbc_count)
        if (bias > maxBias) {
          maxBias = bias
          maxBiasJob = job
        }
      }
    })
    return maxBiasJob
  }

  getJobActions = (job, maxBiasJob) => {
    const { qcResults, allowRunAgain, level, cancelQcJob } = this.props
    if (job.job_id) {
      if (job.wbc_count) {
        // Remove 'Run Again' for individual jobs
        return null
      } else if (job.job_status === 'uploading' || job.job_status === 'imaging') {
        return (
          <button className='btn btn-secondary' onClick={() => cancelQcJob(qcResults.id, job)}>
            Cancel
          </button>
        )
      } else if (job.job_status === constants.JOB_STATUSES.CANCELLED || job.error_message !== null) {
        return (
          <button className='btn btn-secondary' onClick={() => this.rerunQc(qcResults.id, level, job)}>
            Run Again
          </button>
        )
      }
    } else {
      return (
        <button className='btn btn-secondary' onClick={() => this.runQc(qcResults.id, level)}>
          Run
        </button>
      )
    }
  }


  areRetestJobsComplete = (retestJobs) => {
    // Find jobs that have been run (have job_id and wbc_count)
    const completedJobs = retestJobs.filter(job => 
      job && job.job_id && job.wbc_count && typeof job.wbc_count === 'number'
    );
    return completedJobs.length >= 3;
  }

  areAllRetestJobsPassing = (retestJobs, levelMaterial) => {
    // Only check jobs that have been completed
    const completedJobs = retestJobs.filter(job => 
      job && job.job_id && job.wbc_count && typeof job.wbc_count === 'number'
    );
    return completedJobs.length >= 3 && completedJobs.every(job =>
      job.wbc_count > levelMaterial.wbc_min && job.wbc_count < levelMaterial.wbc_max
    );
  }

  getStep2JobSlots = (qcResults, controlMaterial, currentLevel) => {
    const totalOutOfRange = countTotalOutOfRange(qcResults, controlMaterial);
    const allInitialJobsComplete = areAllInitialJobsComplete(qcResults);
    
    if (!allInitialJobsComplete) return null;
    
    // Direct path: d1=2 goes straight to Step 2 using jobs 4-6
    if (totalOutOfRange === 2) {
      return { start: 3, end: 6, pathway: 'direct', description: 'Two test strips failed in initial sample. Running Test 2 Expanded Sample.' };
    }
    
    // Escalation path: d1=1 retest failed, escalate to Step 2
    if (totalOutOfRange === 1) {
      const failingLevel = this.getFailingLevel(qcResults, controlMaterial);
      if (failingLevel) {
        const jobs = qcResults[`${failingLevel}_jobs`] || [];
        const retestJobs = jobs.slice(3, 6);
        const retestComplete = this.areRetestJobsComplete(retestJobs);
        const retestPassing = retestComplete && this.areAllRetestJobsPassing(retestJobs, controlMaterial[failingLevel]);
        
        if (retestComplete && !retestPassing) {
          // Failing level uses jobs 7-9, non-failing levels use jobs 4-6
          const jobRange = currentLevel === failingLevel ? { start: 6, end: 9 } : { start: 3, end: 6 };
          return { 
            ...jobRange, 
            pathway: 'from_retest', 
            description: 'A retest test strip failed. Escalating to Test 2 Expanded Sample.',
            failingLevel: failingLevel
          };
        }
      }
    }
    
    return null;
  }

  getFailingLevel = (qcResults, controlMaterial) => {
    const levels = ['low', 'normal', 'high'];
    for (const level of levels) {
      const jobs = qcResults[`${level}_jobs`] || [];
      const initialJobs = jobs.slice(0, 3);
      const levelMaterial = controlMaterial[level];
      const outOfRangeCount = countOutOfRange(initialJobs, levelMaterial);
      if (outOfRangeCount === 1) {
        return level;
      }
    }
    return null;
  }

  areAllStep2JobsComplete = (qcResults, controlMaterial) => {
    const levels = ['low', 'normal', 'high'];
    return levels.every(level => {
      const jobs = qcResults[`${level}_jobs`] || [];
      // Each level uses different job ranges based on whether it was the failing level
      const levelStep2Info = this.getStep2JobSlots(qcResults, controlMaterial, level);
      if (!levelStep2Info) return true; // If no Step 2 needed for this level, consider it complete
      const step2Jobs = jobs.slice(levelStep2Info.start, levelStep2Info.end);
      return step2Jobs.length === 3 && step2Jobs.every(job => 
        job && job.wbc_count && typeof job.wbc_count === 'number'
      );
    });
  }

  areAllStep2JobsPassing = (qcResults, controlMaterial, jobRange = {start: 3, end: 6}) => {
    const levels = ['low', 'normal', 'high'];
    return levels.every(level => {
      const jobs = qcResults[`${level}_jobs`] || [];
      const step2Jobs = jobs.slice(jobRange.start, jobRange.end);
      const levelMaterial = controlMaterial[level];
      return step2Jobs.every(job =>
        job && typeof job.wbc_count === 'number' &&
        job.wbc_count > levelMaterial.wbc_min && job.wbc_count < levelMaterial.wbc_max
      );
    });
  }

  render () {
    const { qcResults, controlMaterial, flowTimes, level, setFlowTime, runIndividualQc } = this.props;
    const maxBiasJob = this.getMaxBiasJob();
    const allJobs = qcResults[`${level}_jobs`] || [];
    const jobs = allJobs.slice(0, 3); // First 3 jobs are regular
    const retestJobs = allJobs.slice(3, 6); // Jobs 4-6 are retest jobs (for d1=1) OR expanded sample jobs (for d1=2)
    const levelMaterial = controlMaterial[level];
    const outOfRangeCount = countOutOfRange(jobs, levelMaterial);
    const meanWBC = controlMaterial[level].wbc_target || (controlMaterial[level].wbc_min + controlMaterial[level].wbc_max) / 2;
    const allInitialJobsComplete = areAllInitialJobsComplete(qcResults);
    const totalOutOfRange = countTotalOutOfRange(qcResults, controlMaterial);
    
    // For d1=1 scenario (retest)
    const retestJobsComplete = this.areRetestJobsComplete(retestJobs);
    const retestJobsPassing = retestJobsComplete && this.areAllRetestJobsPassing(retestJobs, levelMaterial);
    
    // For Step 2 scenarios (both direct d1=2 and escalated from failed retest)
    const step2Info = this.getStep2JobSlots(qcResults, controlMaterial, level);
    const step2JobRange = step2Info ? { start: step2Info.start, end: step2Info.end } : { start: 3, end: 6 };
    const allStep2JobsComplete = step2Info ? this.areAllStep2JobsComplete(qcResults, controlMaterial) : false;
    const allStep2JobsPassing = allStep2JobsComplete && this.areAllStep2JobsPassing(qcResults, controlMaterial, step2JobRange);

    return (
      <div style={{ paddingTop: 20 }}>
        {/* Global QC Status Messages - Only show for 'low' level to avoid duplication */}
        {level === 'low' && (
          <div>
            {/* Quarantine Section - Show when 3 or more strips fail */}
            {allInitialJobsComplete && totalOutOfRange >= 3 && (
              <div className="alert alert-danger mt-3 mb-4">
                <h6>LVT Failed</h6>
                <p><strong>More than 3 test strips failed in the initial 9-test sample. LVT Failed.</strong></p>
              </div>
            )}
          </div>
        )}
        
        <h5>Level: {_.upperFirst(level)}</h5>
        {/* Initial Results */}
        <h6>Test 1</h6>
        <table className='table table-bordered avoid'>
          <thead className='thead-light'>
            <tr>
              <th scope='col'>Replicate</th>
              <th scope='col'>Flow Time</th>
              <th scope='col'>Job ID</th>
              <th scope='col'>Actions</th>
              <th scope='col'>Status</th>
              <th scope='col'>WBC Measured</th>
              <th scope='col'>WBC Range</th>
              <th scope='col'>PASS / FAIL</th>
            </tr>
          </thead>
          <tbody>
            {_.map([0, 1, 2], (index) => {
              const job = jobs[index] || {};
              let flowTime = undefined;
              if (!_.isEmpty(job)) {
                flowTime = flowTimes[job.job_id];
              }
              const wbcBias = job.wbc_count ? _.round(((job.wbc_count - meanWBC) / meanWBC) * 100, 1) : null;
              const wbcPassed = job.wbc_count >= controlMaterial[level].wbc_min && job.wbc_count <= controlMaterial[level].wbc_max;
              const debouncedSetFlowTime = _.debounce(setFlowTime, 1000);
              return (
                <tr key={index + 1}>
                  <td>{index + 1}</td>
                  <td>
                    <input value={flowTime} disabled={!job.job_id} onChange={(e) => debouncedSetFlowTime(job.job_id, e.target.value)} placeholder='Seconds' />
                  </td>
                  <td>{job ? <Link to={`/jobs/${job.job_id}/images`}>{job.job_id}</Link> : 'No Job'}</td>
                  <td>
                    { this.getJobActions(job, maxBiasJob) }
                  </td>
                  <td>
                    <div>
                      {job.job_status}
                    </div>
                    {
                      _.isEmpty(job) || constants.FINISHED_JOB_STATUSES.indexOf(job.job_status) > -1 ? null : (
                        <div>
                          <BeatLoader loading color={'#6f8eaa'} />
                        </div>
                      )
                    }
                  </td>
                  <td>{job.wbc_count}</td>
                  <td>{controlMaterial[level].wbc_min} - {controlMaterial[level].wbc_max}</td>
                  <td>{job.wbc_count ? (wbcPassed ? 'PASS' : 'FAIL') : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Retest Section - Only show after all 9 initial jobs are complete and only when exactly 1 failure across ALL levels */}
        {allInitialJobsComplete && outOfRangeCount === 1 && totalOutOfRange === 1 && (
          <div>
            <div className="alert alert-warning">
              One test strip failed at {level} level. Please run another 3 strips below.
            </div>
            <h6>Retest Strips</h6>
            <table className='table table-bordered avoid'>
              <thead className='thead-light'>
                <tr>
                  <th scope='col'>Replicate</th>
                  <th scope='col'>Flow Time</th>
                  <th scope='col'>Job ID</th>
                  <th scope='col'>Actions</th>
                  <th scope='col'>Status</th>
                  <th scope='col'>WBC Measured</th>
                  <th scope='col'>WBC Range</th>
                  <th scope='col'>PASS / FAIL</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((idx) => {
                  const job = retestJobs[idx] || {};
                  let flowTime = undefined;
                  if (!_.isEmpty(job)) {
                    flowTime = flowTimes[job.job_id];
                  }
                  const wbcBias = job.wbc_count ? _.round(((job.wbc_count - meanWBC) / meanWBC) * 100, 1) : null;
                  const wbcPassed = job.wbc_count > controlMaterial[level].wbc_min && job.wbc_count < controlMaterial[level].wbc_max;
                  const debouncedSetFlowTime = _.debounce(setFlowTime, 1000);
                  return (
                    <tr key={4 + idx}>
                      <td>{4 + idx}</td>
                      <td>
                        <input value={flowTime} disabled={!job.job_id} onChange={(e) => debouncedSetFlowTime(job.job_id, e.target.value)} placeholder='Seconds' />
                      </td>
                      <td>{job.job_id ? <Link to={`/jobs/${job.job_id}/images`}>{job.job_id}</Link> : 'No Job'}</td>
                      <td>
                        {!job.job_id ? (
                          <button className="btn btn-secondary" onClick={() => this.runQc(qcResults.id, level)}>
                            Run
                          </button>
                        ) : this.getJobActions(job, maxBiasJob)}
                      </td>
                      <td>
                        <div>
                          {job.job_status}
                        </div>
                      </td>
                      <td>{job.wbc_count}</td>
                      <td>{controlMaterial[level].wbc_min} - {controlMaterial[level].wbc_max}</td>
                      <td>{job.wbc_count ? (wbcPassed ? 'PASS' : 'FAIL') : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* d1=1 Retest Results - Show when retest is complete */}
        {allInitialJobsComplete && outOfRangeCount === 1 && totalOutOfRange === 1 && retestJobsComplete && (
          <div>
            {retestJobsPassing ? (
              <div className="alert alert-success mt-3">
                <h6>LVT Pass</h6>
                <p><strong>All 3 retest strips passed at {level} level. LVT Pass.</strong></p>
              </div>
            ) : null}
          </div>
        )}

        {/* Step 2 Expanded Sample Section - Show for both direct (d1=2) and escalated (failed retest) scenarios */}
        {step2Info && (
          <div>
            <div className="alert alert-info">
              {step2Info.description}
            </div>
            <h6>Test 2 Expanded Sample Strips (Jobs {step2Info.start + 1}-{step2Info.end})</h6>
            <table className='table table-bordered avoid'>
              <thead className='thead-light'>
                <tr>
                  <th scope='col'>Replicate</th>
                  <th scope='col'>Flow Time</th>
                  <th scope='col'>Job ID</th>
                  <th scope='col'>Actions</th>
                  <th scope='col'>Status</th>
                  <th scope='col'>WBC Measured</th>
                  <th scope='col'>WBC Range</th>
                  <th scope='col'>PASS / FAIL</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((idx) => {
                  const step2Jobs = allJobs.slice(step2Info.start, step2Info.end);
                  const job = step2Jobs[idx] || {};
                  let flowTime = undefined;
                  if (!_.isEmpty(job)) {
                    flowTime = flowTimes[job.job_id];
                  }
                  const wbcBias = job.wbc_count ? _.round(((job.wbc_count - meanWBC) / meanWBC) * 100, 1) : null;
                  const wbcPassed = job.wbc_count > controlMaterial[level].wbc_min && job.wbc_count < controlMaterial[level].wbc_max;
                  const debouncedSetFlowTime = _.debounce(setFlowTime, 1000);
                  return (
                    <tr key={step2Info.start + 1 + idx}>
                      <td>{step2Info.start + 1 + idx}</td>
                      <td>
                        <input value={flowTime} disabled={!job.job_id} onChange={(e) => debouncedSetFlowTime(job.job_id, e.target.value)} placeholder='Seconds' />
                      </td>
                      <td>{job.job_id ? <Link to={`/jobs/${job.job_id}/images`}>{job.job_id}</Link> : 'No Job'}</td>
                      <td>
                        {!job.job_id ? (
                          <button className="btn btn-secondary" onClick={() => this.runQc(qcResults.id, level)}>
                            Run
                          </button>
                        ) : this.getJobActions(job, maxBiasJob)}
                      </td>
                      <td>
                        <div>
                          {job.job_status}
                        </div>
                      </td>
                      <td>{job.wbc_count}</td>
                      <td>{controlMaterial[level].wbc_min} - {controlMaterial[level].wbc_max}</td>
                      <td>{job.wbc_count ? (wbcPassed ? 'PASS' : 'FAIL') : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}


      </div>
    );
  }
}

export default ResultsView



// WEBPACK FOOTER //
// ./src/routes/QC/components/ResultsView.jsx